/**
 * TranscribeTool - Transcription Launcher
 *
 * Manages media transcription workflows:
 * - Select media items for transcription
 * - Choose transcription type (audio, OCR, caption, description)
 * - Monitor transcription job progress
 * - View and manage transcription versions
 *
 * @module @humanizer/studio/components/tools/TranscribeTool
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  useMedia,
  useMediaSelection,
  type TranscriptionJob,
  type TranscriptionStatus,
  type TranscriptionType as MediaTranscriptionType,
} from '../../contexts/MediaContext';
import { useMediaApi } from '../../hooks/useMediaApi';
import { useBufferSync } from '../../contexts/BufferSyncContext';
import { resolveMediaUrl } from '../../utils/media-url';
import type { TranscriptionType } from './ToolsPane';
import type { SplitContent } from '../workspace/workspace-types';
import { createLogger } from '../../utils/debug';

const log = createLogger('TranscribeTool');

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface TranscribeToolProps {
  /** Archive ID override (if not provided, derived from selected items) */
  archiveId?: string;
  /** Called when transcription is requested (optional — jobs are created via MediaContext regardless) */
  onRequestTranscription?: (mediaId: string, archiveId: string, type: TranscriptionType) => void;
  /** Called to show split view with transcription result */
  onSplitRequested?: (content: SplitContent) => void;
  /** Optional class name */
  className?: string;
}

interface TranscriptionOption {
  id: TranscriptionType;
  label: string;
  icon: string;
  description: string;
  supportedTypes: ('audio' | 'video' | 'image')[];
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

const TRANSCRIPTION_OPTIONS: TranscriptionOption[] = [
  {
    id: 'audio',
    label: 'Audio Transcription',
    icon: '🎙️',
    description: 'Convert speech to text using Whisper',
    supportedTypes: ['audio', 'video'],
  },
  {
    id: 'ocr',
    label: 'Text Extraction (OCR)',
    icon: '📄',
    description: 'Extract text from images',
    supportedTypes: ['image'],
  },
  {
    id: 'caption',
    label: 'Image Caption',
    icon: '🏷️',
    description: 'Generate a brief description of the image',
    supportedTypes: ['image'],
  },
  {
    id: 'description',
    label: 'Detailed Description',
    icon: '📝',
    description: 'Generate a detailed description of visual content',
    supportedTypes: ['image', 'video'],
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

function getStatusColor(status: TranscriptionStatus): string {
  switch (status) {
    case 'completed':
      return 'var(--color-success)';
    case 'processing':
      return 'var(--color-info)';
    case 'pending':
      return 'var(--color-warning)';
    case 'failed':
      return 'var(--color-error)';
    case 'cancelled':
      return 'var(--studio-text-tertiary)';
    default:
      return 'var(--studio-text-tertiary)';
  }
}

function formatStatus(status: TranscriptionStatus): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function TranscribeTool({
  archiveId,
  onRequestTranscription,
  onSplitRequested,
  className = '',
}: TranscribeToolProps): React.ReactElement {
  // Media context
  const { state, getJobsForMedia, addJob, updateJob } = useMedia();
  const { selectedItems, selectedCount } = useMediaSelection();
  const mediaApi = useMediaApi();
  const { importTranscription, getActiveBufferId } = useBufferSync();

  // State
  const [selectedType, setSelectedType] = useState<TranscriptionType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Batch processing state
  const [batchProgress, setBatchProgress] = useState<{ total: number; completed: number; failed: number } | null>(null);
  const cancelledRef = useRef(false);

  // Get active jobs
  const activeJobs = useMemo(() => {
    return Array.from(state.transcriptionJobs.values())
      .filter((job) => job.status === 'pending' || job.status === 'processing')
      .sort((a, b) => (a.startedAt?.getTime() ?? 0) - (b.startedAt?.getTime() ?? 0));
  }, [state.transcriptionJobs]);

  // Get available transcription options based on selected media types
  const availableOptions = useMemo(() => {
    if (selectedItems.length === 0) return TRANSCRIPTION_OPTIONS;

    const selectedTypes = new Set(selectedItems.map((item) => item.type));
    return TRANSCRIPTION_OPTIONS.filter((option) =>
      option.supportedTypes.some((type) => selectedTypes.has(type))
    );
  }, [selectedItems]);

  // Check if selection supports transcription
  const canTranscribe = selectedCount > 0 && selectedType !== null;

  // Handle cancelling remaining batch items
  const handleCancelBatch = useCallback(() => {
    cancelledRef.current = true;
  }, []);

  // Handle transcription request — processes sequentially for concurrency=1
  const handleStartTranscription = useCallback(async () => {
    if (!canTranscribe || !selectedType) return;

    setIsSubmitting(true);
    setError(null);
    cancelledRef.current = false;

    try {
      // Snapshot active buffer ID at click time so async results land in the correct buffer
      const capturedBufferId = getActiveBufferId();

      // Filter to compatible items
      const option = TRANSCRIPTION_OPTIONS.find((o) => o.id === selectedType);
      const compatibleItems = selectedItems.filter(
        (item) => option?.supportedTypes.includes(item.type)
      );

      if (compatibleItems.length === 0) {
        setError('No compatible media items for this transcription type');
        return;
      }

      const total = compatibleItems.length;
      let completed = 0;
      let failed = 0;

      if (total > 1) {
        setBatchProgress({ total, completed: 0, failed: 0 });
      }

      // Process sequentially — Ollama vision serializes anyway
      for (const item of compatibleItems) {
        if (cancelledRef.current) {
          log.info(`Batch cancelled after ${completed}/${total}`);
          break;
        }

        // Create job
        const job: TranscriptionJob = {
          id: crypto.randomUUID(),
          mediaId: item.id,
          type: selectedType as MediaTranscriptionType,
          status: 'pending',
          startedAt: new Date(),
        };

        addJob(job);
        updateJob(job.id, { status: 'processing', progress: 0 });

        const itemArchiveId = archiveId ?? item.archiveId;

        // Notify parent if callback provided
        if (onRequestTranscription && itemArchiveId) {
          onRequestTranscription(item.id, itemArchiveId, selectedType);
        }

        try {
          const result: { text?: string; versionId?: string; model?: string } =
            await mediaApi.transcribe(item.id, selectedType, itemArchiveId);

          updateJob(job.id, { status: 'completed', text: result?.text, versionId: result?.versionId });
          completed++;

          if (result?.text && onSplitRequested) {
            const mediaUrl = resolveMediaUrl(`/api/media/serve/${item.id}`);
            onSplitRequested({
              original: mediaUrl,
              transformed: `*Model: ${result.model ?? 'unknown'}*\n\n${result.text}`,
              originalLabel: 'Media',
              transformedLabel: 'Transcription',
              originalRenderMode: 'media',
              mediaType: item.type as 'audio' | 'video' | 'image',
            });
          }

          // Persist transcription to AUI buffer
          if (result?.text && result?.versionId) {
            importTranscription(item.id, result.text, {
              type: selectedType!,
              versionId: result.versionId,
              model: result.model ?? 'unknown',
              filename: item.filename,
            }, capturedBufferId ?? undefined).catch((err: unknown) => log.error('Buffer import failed', err));
          }
        } catch (err) {
          updateJob(job.id, {
            status: 'failed',
            error: err instanceof Error ? err.message : 'Transcription failed',
          });
          failed++;
        }

        if (total > 1) {
          setBatchProgress({ total, completed, failed });
        }
      }

      // Reset
      setSelectedType(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start transcription');
    } finally {
      setIsSubmitting(false);
      setBatchProgress(null);
    }
  }, [canTranscribe, selectedType, archiveId, selectedItems, addJob, updateJob, mediaApi, onRequestTranscription, importTranscription, getActiveBufferId, onSplitRequested]);

  return (
    <div className={`transcribe-tool ${className}`}>
      {/* Selection Status */}
      <div className="transcribe-tool__selection">
        {selectedCount > 0 ? (
          <div className="transcribe-tool__selection-info">
            <span className="transcribe-tool__selection-count">
              {selectedCount} media item{selectedCount !== 1 ? 's' : ''} selected
            </span>
            <div className="transcribe-tool__selection-types">
              {Array.from(new Set(selectedItems.map((item) => item.type))).map((type) => (
                <span key={type} className={`transcribe-tool__type-badge transcribe-tool__type-badge--${type}`}>
                  {type}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <span className="transcribe-tool__selection-empty">
            Select media to transcribe
          </span>
        )}
      </div>

      {/* Transcription Options */}
      <div className="transcribe-tool__options">
        <div className="transcribe-tool__options-header">Transcription Type</div>
        <div className="transcribe-tool__options-list" role="listbox" aria-label="Transcription types">
          {availableOptions.map((option) => (
            <button
              key={option.id}
              className={`transcribe-option ${selectedType === option.id ? 'transcribe-option--selected' : ''}`}
              onClick={() => setSelectedType(option.id)}
              disabled={selectedCount === 0}
              role="option"
              aria-selected={selectedType === option.id}
            >
              <span className="transcribe-option__icon" aria-hidden="true">
                {option.icon}
              </span>
              <div className="transcribe-option__info">
                <span className="transcribe-option__label">{option.label}</span>
                <span className="transcribe-option__description">{option.description}</span>
              </div>
              <div className="transcribe-option__types">
                {option.supportedTypes.map((type) => (
                  <span key={type} className="transcribe-option__supported-type">
                    {type}
                  </span>
                ))}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="transcribe-tool__error" role="alert">
          <span aria-hidden="true">⚠️</span>
          {error}
        </div>
      )}

      {/* Start Button */}
      <div className="transcribe-tool__actions">
        <button
          className="transcribe-tool__btn transcribe-tool__btn--primary"
          onClick={handleStartTranscription}
          disabled={!canTranscribe || isSubmitting}
        >
          {isSubmitting
            ? (batchProgress ? `Processing ${batchProgress.completed + 1}/${batchProgress.total}...` : 'Processing...')
            : `Start Transcription${selectedCount > 1 ? ` (${selectedCount})` : ''}`}
        </button>
        {isSubmitting && batchProgress && (
          <button
            className="transcribe-tool__btn transcribe-tool__btn--cancel"
            onClick={handleCancelBatch}
          >
            Cancel Remaining
          </button>
        )}
      </div>

      {/* Batch Progress */}
      {batchProgress && (
        <div className="transcribe-tool__batch-progress">
          <div className="transcribe-tool__batch-progress-bar">
            <div
              className="transcribe-tool__batch-progress-fill"
              style={{ width: `${Math.round(((batchProgress.completed + batchProgress.failed) / batchProgress.total) * 100)}%` }}
            />
          </div>
          <span className="transcribe-tool__batch-progress-text">
            {batchProgress.completed} completed
            {batchProgress.failed > 0 && `, ${batchProgress.failed} failed`}
            {' '}of {batchProgress.total}
          </span>
        </div>
      )}

      {/* Active Jobs */}
      {activeJobs.length > 0 && (
        <div className="transcribe-tool__jobs">
          <div className="transcribe-tool__jobs-header">
            Active Jobs ({activeJobs.length})
          </div>
          <div className="transcribe-tool__jobs-list">
            {activeJobs.map((job) => {
              const mediaItem = state.items.get(job.mediaId);
              return (
                <div key={job.id} className="transcribe-job">
                  <div className="transcribe-job__info">
                    <span className="transcribe-job__name">
                      {mediaItem?.filename ?? 'Unknown media'}
                    </span>
                    <span className="transcribe-job__type">{job.type}</span>
                  </div>
                  <div className="transcribe-job__status">
                    <span
                      className="transcribe-job__status-indicator"
                      style={{ backgroundColor: getStatusColor(job.status) }}
                    />
                    <span className="transcribe-job__status-text">
                      {formatStatus(job.status)}
                    </span>
                    {job.progress !== undefined && (
                      <span className="transcribe-job__progress">
                        {Math.round(job.progress)}%
                      </span>
                    )}
                  </div>
                  {job.status === 'processing' && job.progress !== undefined && (
                    <div className="transcribe-job__progress-bar">
                      <div
                        className="transcribe-job__progress-fill"
                        style={{ width: `${job.progress}%` }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {selectedCount === 0 && activeJobs.length === 0 && (
        <div className="transcribe-tool__placeholder">
          <span aria-hidden="true">🎙️</span>
          <p>Transcribe your media</p>
          <p className="transcribe-tool__placeholder-hint">
            Select media items from the gallery to transcribe audio, extract text, or generate descriptions
          </p>
        </div>
      )}
    </div>
  );
}

export default TranscribeTool;
